import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { ProductRow } from "@/modules/catalog/domain/types";
import { AppImage } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import { confirm } from "@/shared/lib/confirm";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";



import {
  CoffeeIcon,
  EditIcon,
  PackageIcon,
  TrashIcon,
} from "lucide-react";

type ProductsTableProps = {
  data: ProductRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (row: ProductRow) => void;
  onDelete: (id: string) => void;
};

export function ProductsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: ProductsTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<ProductRow>[] = [
    createSortableColumn<ProductRow>("type", "ປະເພດ", {
      size: 90,
      cell: ({ row }) => {
        return (
          <Badge
            variant={row.original.type === "MENU" ? "default" : "secondary"}
          >
            {row.original.type === "MENU" ? (
              <CoffeeIcon className="h-4 w-4" />
            ) : (
              <PackageIcon className="h-4 w-4" />
            )}
            {row.original.type === "MENU" ? "ເມນູ" : "ສິນຄ້າ"}
          </Badge>
        );
      },
    }),
    {
      ...createSortableColumn<ProductRow>("name", "ຊື່", { size: 260 }),
      cell: ({ row }) => {
        const { id, name, imageUrl } = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="size-10 shrink-0 overflow-hidden rounded-lg border bg-muted">
              <AppImage
                src={imageUrl}
                alt=""
                className="size-full"
                fit="cover"
                showLoading={false}
                fallback={
                  <div className="flex size-full items-center justify-center text-muted-foreground text-xs">
                    —
                  </div>
                }
              />
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium">{name}</p>
              <p className="text-muted-foreground text-xs">{id}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: "variantCount",
      header: "ຈຳນວນ variant",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.variantCount}
        </span>
      ),
      size: 100,
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="default">ເປີດ</Badge>
        ) : (
          <Badge variant="secondary">ປິດ</Badge>
        ),
      size: 80,
    },
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: ProductRow } }) => {
        const id = row.original.id;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has("catalog:update")) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }
        if (has("catalog:delete"))
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບສິນຄ້າ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບສິນຄ້ານີ້?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) onDelete(id);
            },
          });

        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <DataTable<ProductRow, unknown>
      noDataMessage="ບໍ່ພົບສິນຄ້າ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
